import { appstr } from "@coreapp/utils/appstr";
import { Flex, OverflowText, Text } from "@theorgchartdev/storybook";
import { sanitizeBlockedValue } from "@coreapp/utils/FormatFieldValue";
import { FormatPositionLabel } from "@coreapp/utils/FormatPositionLabel";
import { GetWSMgr } from "@/Utils/StateLib";
import { OverrideFactory } from "@coreapp/CoreApp/OverrideFactory";
import type { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";

export const promptToUnassignEmployees = (
  payload: QObjDBObject | QObjDBObject[],
  partialUnassign?: boolean
) => {
  const wsMgr = GetWSMgr();

  let messagingMode: "all" | "single" | "mixed" | null = null;
  if (Array.isArray(payload)) {
    messagingMode = "mixed";
  } else {
    if (payload.IsHolderObj()) {
      messagingMode = "single";
    } else if (payload.IsPositionObj()) {
      const holders = payload.GetHolderList() ?? [];
      messagingMode = holders.length === 1 ? "single" : "all";
    }

    payload = [payload];
  }

  if (!messagingMode) {
    // Invalid payload
    return;
  }

  // Map of holders objects with their position as the key
  const positions: Record<string, QObjDBObject> = {};
  const holders: Record<string, QObjDBObject[]> = {};

  for (const dataObj of payload) {
    let positionObj: QObjDBObject | null = null;
    let holdersList: QObjDBObject[] = [];

    if (dataObj.IsHolderObj()) {
      positionObj = dataObj.GetParentPosition();
      holdersList = [dataObj];
    } else if (dataObj.IsPositionObj()) {
      positionObj = dataObj;
      holdersList = dataObj.GetHolderList() ?? [];
    }

    if (!positionObj || !holdersList.length) {
      continue;
    }

    const positionID = `${positionObj.GetObjectRecordID()}`;
    positions[positionID] = positionObj;

    const previousHolders = holders[positionID] ?? [];
    holders[positionID] = [...previousHolders, ...holdersList];
  }

  let header = "";
  let contentTemplateKey = "";
  let contentReplaceDict = {};
  let contentNode: React.ReactNode = null;

  if (messagingMode === "single") {
    header = appstr("Unassign employee");
    contentTemplateKey = "Are you sure you want to unassign employee from the position position?";

    const positionID = Object.keys(positions)[0];
    const positionObj = positions[positionID];
    const [holderObj] = holders[positionID];

    contentReplaceDict = {
      employee: `${sanitizeBlockedValue(holderObj.SmartGetFieldValue("Name"))}`,
      position: FormatPositionLabel(positionObj)
    };
  } else {
    const totalHoldersCount = partialUnassign
      ? Object.keys(positions).reduce((acc, positionID) => {
          return acc + (holders[positionID]?.length ?? 0);
        }, 0)
      : appstr("All").toLocaleLowerCase();

    contentReplaceDict = {
      totalHoldersCountEmployees: `${totalHoldersCount} ${appstr(totalHoldersCount === 1 ? "Employee" : "Employees").toLocaleLowerCase()}`
    };

    if (messagingMode === "all") {
      header = appstr("Unassign employees");
      contentTemplateKey =
        "Are you sure you want to unassign totalHoldersCount employees from the position position?";

      const positionID = Object.keys(positions)[0];
      const positionObj = positions[positionID];
      const holderObjs = holders[positionID];

      contentReplaceDict = {
        ...contentReplaceDict,
        position: FormatPositionLabel(positionObj)
      };

      contentNode = (
        <ul className="oc-list-disc">
          {holderObjs.map(dataObj => (
            <li key={`${dataObj.GetObjectRecordID()}`}>
              <OverflowText>{`${sanitizeBlockedValue(dataObj.SmartGetFieldValue("Name"))}`}</OverflowText>
            </li>
          ))}
        </ul>
      );
    } else if (messagingMode === "mixed") {
      header = appstr("Unassign employees");

      contentTemplateKey =
        "Are you sure you want to unassign totalHoldersCount employees from the following positions?";

      contentNode = (
        <ul className="oc-list-disc">
          {Object.keys(positions).map(positionID => {
            const positionObj = positions[positionID];
            const holdersList = holders[positionID];

            return (
              <li key={positionID}>
                <OverflowText modifier="bold">{FormatPositionLabel(positionObj)}</OverflowText>
                <ul className="oc-list-disc">
                  {holdersList.map(dataObj => (
                    <li key={`${dataObj.GetObjectRecordID()}`}>
                      <OverflowText>{`${sanitizeBlockedValue(dataObj.SmartGetFieldValue("Name"))}`}</OverflowText>
                    </li>
                  ))}
                </ul>
              </li>
            );
          })}
        </ul>
      );
    }
  }

  wsMgr?.CreateReactAlert(
    header,
    <Flex direction="column" width="100%" gap="2px">
      <Text
        dynamic={{
          replaceDict: contentReplaceDict,
          formatConfig: Object.fromEntries(
            Object.keys(contentReplaceDict).map(key => [key, { type: "bold" }])
          ),
          template: appstr(contentTemplateKey)
        }}
      >
        {appstr(contentTemplateKey, contentReplaceDict)}
      </Text>
      {contentNode}
    </Flex>,
    async () => {
      const holdersList = Object.values(holders).flat();
      await OverrideFactory.UnassignHolders(holdersList, partialUnassign);
    },
    () => {},
    appstr("Unassign"),
    appstr("Cancel"),
    () => {
      /* Dismiss */
    },
    {
      alertProps: {
        severity: "info",
        message: appstr("Unassigned employees can be found in the Employee List")
      }
    },
    "alert-unassign-holder"
  );
};
